import { Helmet } from "react-helmet-async";

const SITE_URL = "https://onlinesicher24.de";
const DEFAULT_OG = `${SITE_URL}/og-image.png`;

interface SEOProps {
  title: string;
  description: string;
  /** Path beginning with "/", e.g. "/impressum". Defaults to current pathname. */
  canonicalPath?: string;
  ogImage?: string;
  noindex?: boolean;
  /** Optional JSON-LD structured data (object or array of objects). */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  /** Optional override for og:type, default "website". */
  ogType?: string;
}

/**
 * Per-page SEO meta tags with canonical URL, Open Graph + Twitter cards
 * and optional JSON-LD structured data.
 */
const SEO = ({
  title,
  description,
  canonicalPath,
  ogImage = DEFAULT_OG,
  noindex = false,
  jsonLd,
  ogType = "website",
}: SEOProps) => {
  const path =
    canonicalPath ??
    (typeof window !== "undefined" ? window.location.pathname : "/");
  const canonical = `${SITE_URL}${path}`;
  const jsonLdArray = jsonLd
    ? Array.isArray(jsonLd)
      ? jsonLd
      : [jsonLd]
    : [];

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      <meta
        name="robots"
        content={noindex ? "noindex, nofollow" : "index, follow"}
      />

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="OnlineSicher24" />
      <meta property="og:locale" content="de_DE" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {jsonLdArray.map((data, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
